import {NullableStation, Station} from "../stations/station.service.interface";
import {IFlightSearchService, IStartBookingOptions,} from "./flight-search.service.interface";
import {IServiceFactory} from "../service-factory.interface";
import {makeObservable, observable, reaction, runInAction} from "mobx";
import {Wizard} from "../../models/wizard/wizard";
import {ServiceBase} from "../service-base";
import {UserLocalStorageKeys} from "../storage/user-local-storage/user-local-storage-keys";
import {NullableDate} from "../../types/nullable-types";
import {TimeSpan} from "../../types/time-span";
import {
    IFlightSearchControllerViewModel,
} from "./flight-search-controller/flight-search-controller-view-model.interface";
import {FlightSearchControllerModel} from "./flight-search-controller/flight-search-controller.model";
import {FlightSearchDeepLinkHandler} from "./flight-search-deep-link-handler";
import {PsoUserAgreementEnum, PsoUserOptionsEnum} from "./flight-search-controller/pso.enums";
import {RoundTripLowFareReaderFactory} from "../low-fare/round-trip-low-fare-reader-factory";
import {IRoundTripLowFareReader} from "../low-fare/low-fare-readers/low-fare-reader.interface";

import {UserSessionStorageKeys} from "../storage/user-session-storage/user-session-storage-keys";
import {DialogCloseButtonBehavior} from "../dialog/dialog-enums";

export abstract class FlightSearchBaseService extends ServiceBase implements IFlightSearchService {
    constructor(services: IServiceFactory) {
        super(services);

        this._deepLinkHandler = new FlightSearchDeepLinkHandler(this.services, () => this.getLowFaresReader());

        this.searchController = new FlightSearchControllerModel(this.services, UserSessionStorageKeys.FlightSearchSearchControllerStorage, {
            getLowFareReader: () => this.getLowFaresReader()
        });

        makeObservable(this, {
            searchController: observable.ref,
        })

        this._subscribeToReactions();

    }

    searchController: IFlightSearchControllerViewModel;
    private _appDeactivationTime: NullableDate = null;
    protected readonly _deepLinkHandler: FlightSearchDeepLinkHandler;

    private _lowFareReaderFactory: RoundTripLowFareReaderFactory | null = null;
    getLowFaresReader(): IRoundTripLowFareReader {
        if(!this._lowFareReaderFactory) {
            this._lowFareReaderFactory = new RoundTripLowFareReaderFactory(this.services,
                this.searchController,
                false,
                () => this.services.currency.current);
        }
        return this._lowFareReaderFactory.lowFareReader;

    }

    protected abstract _goToFlightSelection(options?: IStartBookingOptions): Promise<void>;
    protected abstract _createSteps(): Wizard;
    protected abstract _onSearchMaskErrors(): void;
    abstract startWizardSearch(): void;
    get hasDeepLinkErrors(): boolean {
        return this._deepLinkHandler.hasDeepLinkErrors;
    }

    resetFields(): void {
        this.searchController.resetFields();
    }

    private _steps: Wizard | null = null;
    get steps(): Wizard {
        if(!this._steps) {
            this._steps = this._createSteps();
        }
        return this._steps;
    }

    private _subscribeToReactions(): void {

        reaction(() => this.services.user.isAuthorized, () => {
            if(!this.services.user.isAuthorized) {
                this.resetFields();
            }
        })

        reaction(() => this.services.application.isActive,
            async (isActive) => {
                if(isActive) {
                    if(this._appDeactivationTime && this.services.navigator.routes.flightSearch.hasActiveChildRoute) {
                        const timeSinceAppDeactivate = TimeSpan.fromMilliseconds(Date.now() - this._appDeactivationTime.getTime());
                        if(this.services.configuration.dotRezSessionTimeout.totalMilliseconds < timeSinceAppDeactivate.totalMilliseconds) {
                            await this.services.navigator.goHome({
                                skipRoutingGuards: true
                            });
                        }
                    }
                    this._appDeactivationTime = null;
                } else {
                    this._appDeactivationTime = this.services.time.currentDate;
                }
            });

        reaction(() => this.services.navigator.currentRoute,
            async () => {
                try {
                    const searchController = await this._deepLinkHandler.createSearchController();
                    if(searchController) {
                        runInAction(() => {
                            this.searchController?.dispose();
                            this.searchController = searchController;
                        });

                        await this.startBooking({
                            useRouteReplace: true
                        });
                    }
                } catch(err) {
                    this.services.logger.error('Deep link search failed', err);
                    await this.services.alert.showErrorAsync(this.services.language.translate('Oops! Something went wrong.'));
                    await this.services.navigator.goHome({
                        skipRoutingGuards: true
                    });
                }
            },
            {
                fireImmediately: true
            });

    }

    private _getRecentlySearch(key: UserLocalStorageKeys.FlightSearchRecentlySearchedOriginStations | UserLocalStorageKeys.FlightSearchRecentlySearchedDestinationStations): Station[] {
        let result: Station[] = [];
        const savedStations = this.services.userLocalStorage.getJson<string[]>(key) || [];

        for(let stationCode of savedStations) {
            const s = this.services.stations.tryGetStation(stationCode);
            if(s) {
                result.push(s);
            }
        }

        return result;
    }

    getRecentlySearchedOrigins(): Station[] {
        return this._getRecentlySearch(UserLocalStorageKeys.FlightSearchRecentlySearchedOriginStations);
    }
        
    getRecentlySearchedDestinations(): Station[] {
        return this._getRecentlySearch(UserLocalStorageKeys.FlightSearchRecentlySearchedDestinationStations);
    }        
        
    private _saveRecentlySearch(key: UserLocalStorageKeys.FlightSearchRecentlySearchedOriginStations | UserLocalStorageKeys.FlightSearchRecentlySearchedDestinationStations, station: NullableStation) {
        if(!station) {
            return;
        }
        let recentlySearch = this.services.userLocalStorage.getJson<string[]>(key) || [];
        const stationIndex = recentlySearch.indexOf(station.stationCode);

        if (stationIndex >= 0) {
            recentlySearch.splice(stationIndex, 1);
        }

        recentlySearch.splice(0, 0, station.stationCode); 

        recentlySearch = recentlySearch.slice(0, 3);

        this.services.userLocalStorage.setJson(key, recentlySearch);
    }

    async startBooking(options?: IStartBookingOptions): Promise<void> {
        const errors = this.searchController.activateErrorsValidation();
        if(errors.length > 0) {
            this._onSearchMaskErrors();
            return;
        }

        if(this.searchController.passengers.countDogs() > 0 && this.searchController.psoUserOption !== PsoUserOptionsEnum.None) {
            await this.services.dialogFactory.showSimpleMessageDialog({
                closeButtonBehavior: DialogCloseButtonBehavior.None,
                title: this.services.language.translate('Attention'),
                message: this.services.language.translate('You cannot book Territorial Continuity online when you travel with a dog. However, you can request this when our staff will contact you after you finish this booking.'),
                buttonText: this.services.language.translate('OK'),
            });
            this.searchController.psoUserOption = PsoUserOptionsEnum.None;
        }

        await this._goToFlightSelection(options);
        //reset the user agreement so on next search to ask the user again about special price T&C
        this.searchController.psoUserAgreement = PsoUserAgreementEnum.None;
        //basically here we reset the deep link agent so it will not persist for the next search
        this.searchController.setAnonymousAgentName(null);


        this._saveRecentlySearch(UserLocalStorageKeys.FlightSearchRecentlySearchedOriginStations, this.searchController.departureOrigin);
        this._saveRecentlySearch(UserLocalStorageKeys.FlightSearchRecentlySearchedDestinationStations, this.searchController.departureDestination);
    }

    switchStations() {
        let tempStation = this.searchController.departureOrigin;
        let departureDate = this.searchController.departureDate;
        let returnDate = this.searchController.returnDate;
        let psoUserOption = this.searchController.psoUserOption;
        this.searchController.departureOrigin = this.searchController.departureDestination;
        this.searchController.departureDestination = tempStation;
        this.searchController.departureDate = departureDate;
        this.searchController.returnDate = returnDate;
        this.searchController.psoUserOption = psoUserOption;
    }
}
